/**
 * Postfach-Übergabe — Passwort-geschützte Zusammenfassung eines Postfachs
 *
 * Für neue Mitarbeiter, die Objekte eines Kollegen übernehmen.
 * Zeigt alle Mails sortiert nach Objekt mit AI-Zusammenfassungen.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import DOMPurify from 'dompurify';
import './PostfachUebergabe.css';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// ── Hilfsfunktionen ──

function formatDate(isoStr) {
  if (!isoStr) return '-';
  const d = new Date(isoStr);
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatDateTime(isoStr) {
  if (!isoStr) return '-';
  const d = new Date(isoStr);
  return d.toLocaleDateString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function timeAgo(isoStr) {
  if (!isoStr) return '';
  const diff = Date.now() - new Date(isoStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `vor ${mins} Min.`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `vor ${hours} Std.`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `vor ${days} Tagen`;
  const months = Math.floor(days / 30);
  return `vor ${months} Mon.`;
}


// ── Passwort-Gate ──

function PasswordGate({ onAuth }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password.trim()) return;
    setLoading(true);
    setError('');

    try {
      const res = await fetch(`${API_URL}/postfach/auth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Zugriff verweigert');
      }
      const data = await res.json();
      onAuth(data.token, data.mailbox);
    } catch (err) {
      setError(err.message || 'Verbindungsfehler');
      setPassword('');
      inputRef.current?.focus();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pu-gate">
      <div className="pu-gate__card">
        <div className="pu-gate__logo">
          <img src="/assets/logos/one-immo-lockup-on-light.svg" alt="One. immo" />
        </div>
        <h1 className="pu-gate__title">Postfach-Übergabe</h1>
        <p className="pu-gate__subtitle">
          Zugriff auf die Postfach-Zusammenfassung zur Einarbeitung und Objekt-Übergabe.
        </p>
        <form onSubmit={handleSubmit} className="pu-gate__form">
          <label className="pu-gate__label">Zugangspasswort</label>
          <input
            ref={inputRef}
            type="password"
            className="pu-gate__input"
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="Passwort eingeben..."
            disabled={loading}
            autoComplete="off"
          />
          {error && <div className="pu-gate__error">{error}</div>}
          <button type="submit" className="pu-gate__btn" disabled={loading || !password.trim()}>
            {loading ? 'Prüfe...' : 'Zugriff anfordern'}
          </button>
        </form>
        <p className="pu-gate__hint">
          Passwort erhalten Sie von Ihrer Teamleitung.
        </p>
      </div>
    </div>
  );
}


// ── KPI-Karten ──

function StatsBar({ stats }) {
  const kpis = [
    { label: 'Mails gesamt', value: stats.total_mails, accent: '#00a87e' },
    { label: 'Empfangen', value: stats.received, accent: '#10b981' },
    { label: 'Gesendet', value: stats.sent, accent: '#8b5cf6' },
    { label: 'Objekte', value: stats.objekte_zugeordnet, accent: '#f59e0b' },
    { label: 'Dringend', value: stats.dringende_objekte, accent: stats.dringende_objekte > 0 ? '#ef4444' : '#6b7280' },
    { label: 'Ordner', value: stats.folders_scanned, accent: '#6b7280' },
  ];

  return (
    <div className="pu-stats">
      {kpis.map(k => (
        <div key={k.label} className="pu-stats__card" style={{ borderLeftColor: k.accent }}>
          <div className="pu-stats__value">{k.value}</div>
          <div className="pu-stats__label">{k.label}</div>
        </div>
      ))}
    </div>
  );
}


// ── Mail-Zeile in der Timeline ──

function MailRow({ mail, onSelect }) {
  const isSent = mail.direction === 'sent';
  return (
    <div className={`pu-mail ${isSent ? 'pu-mail--sent' : 'pu-mail--received'}`} onClick={() => onSelect(mail)}>
      <div className="pu-mail__indicator" title={isSent ? 'Gesendet' : 'Empfangen'}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          {isSent
            ? <><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></>
            : <><polyline points="22 12 16 12 14 15 10 15 8 12 2 12"/><path d="M5.45 5.11L2 12v6a2 2 0 002 2h16a2 2 0 002-2v-6l-3.45-6.89A2 2 0 0016.76 4H7.24a2 2 0 00-1.79 1.11z"/></>
          }
        </svg>
      </div>
      <div className="pu-mail__content">
        <div className="pu-mail__header">
          <span className="pu-mail__subject">{mail.subject}</span>
          <div className="pu-mail__meta">
            {mail.has_attachments && <span className="pu-mail__tag pu-mail__tag--attachment" title="Anhang">&#128206;</span>}
            {mail.importance === 'high' && <span className="pu-mail__tag pu-mail__tag--high">Wichtig</span>}
            <span className="pu-mail__date">{formatDateTime(mail.date)}</span>
          </div>
        </div>
        <div className="pu-mail__detail">
          <span className="pu-mail__from">
            {isSent ? `An: ${mail.to?.[0] || '-'}` : `Von: ${mail.from_name || mail.from_email}`}
          </span>
          <span className="pu-mail__ago">{timeAgo(mail.date)}</span>
        </div>
        {mail.preview && <div className="pu-mail__preview">{mail.preview}</div>}
      </div>
    </div>
  );
}


// ── Objekt-Karte mit Timeline ──

function ObjektCard({ objekt, onSelectMail, defaultExpanded }) {
  const [expanded, setExpanded] = useState(defaultExpanded || false);

  return (
    <div className={`pu-objekt ${objekt.dringend ? 'pu-objekt--urgent' : ''}`}>
      <div className="pu-objekt__header" onClick={() => setExpanded(!expanded)}>
        <div className="pu-objekt__title-row">
          {objekt.dringend && <span className="pu-objekt__badge pu-objekt__badge--urgent">Dringend</span>}
          <h3 className="pu-objekt__title">
            {objekt.adresse || `Objekt ${objekt.objekt_nr}`}
          </h3>
          <span className="pu-objekt__nr">#{objekt.objekt_nr}</span>
        </div>
        <div className="pu-objekt__sub-row">
          <span className="pu-objekt__count">{objekt.mail_count} Mails</span>
          <svg className={`pu-objekt__chevron ${expanded ? 'pu-objekt__chevron--open' : ''}`}
            width="16" height="16" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </div>
      </div>

      {/* AI-Zusammenfassung */}
      <div className="pu-objekt__summary">
        <p>{objekt.zusammenfassung}</p>
        {objekt.offene_vorgaenge?.length > 0 && (
          <div className="pu-objekt__open">
            <strong>Offene Vorgänge:</strong>
            <ul>
              {objekt.offene_vorgaenge.map((v, i) => <li key={i}>{v}</li>)}
            </ul>
          </div>
        )}
      </div>

      {/* Mail-Timeline */}
      {expanded && (
        <div className="pu-objekt__timeline">
          {objekt.mails.map((mail, i) => (
            <MailRow key={mail.id || i} mail={mail} onSelect={onSelectMail} />
          ))}
        </div>
      )}
    </div>
  );
}


// ── Mail-Detail-Modal ──

function formatBytes(n) {
  if (!n) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function MailDetail({ mail, onClose }) {
  const [fullMail, setFullMail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [attachments, setAttachments] = useState([]);
  const [downloadingId, setDownloadingId] = useState(null);
  const [attachmentError, setAttachmentError] = useState('');

  const token = sessionStorage.getItem('postfach_token');

  useEffect(() => {
    if (!mail?.id) return;
    setLoading(true);
    setAttachments([]);
    setAttachmentError('');
    fetch(`${API_URL}/postfach/message/${mail.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => { setFullMail(data); setLoading(false); })
      .catch(() => setLoading(false));

    if (mail.has_attachments) {
      fetch(`${API_URL}/postfach/message/${mail.id}/attachments`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(r => r.ok ? r.json() : Promise.reject(r))
        .then(data => setAttachments(data.attachments || []))
        .catch(() => setAttachmentError('Anhänge konnten nicht geladen werden.'));
    }
  }, [mail?.id, mail?.has_attachments, token]);

  const handleDownload = async (att) => {
    setDownloadingId(att.id);
    setAttachmentError('');
    try {
      const res = await fetch(
        `${API_URL}/postfach/message/${mail.id}/attachment/${att.id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail.detail || `Fehler ${res.status}`);
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = att.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setAttachmentError(`Download fehlgeschlagen: ${err.message}`);
    } finally {
      setDownloadingId(null);
    }
  };

  if (!mail) return null;

  return (
    <div className="pu-modal-overlay" onClick={onClose}>
      <div className="pu-modal" onClick={e => e.stopPropagation()}>
        <div className="pu-modal__header">
          <h2 className="pu-modal__subject">{mail.subject}</h2>
          <button className="pu-modal__close" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
        <div className="pu-modal__meta">
          <div><strong>Von:</strong> {fullMail?.from?.name || mail.from_name} &lt;{fullMail?.from?.email || mail.from_email}&gt;</div>
          <div><strong>An:</strong> {(fullMail?.to || []).map(r => r.email || r).join(', ') || mail.to?.join(', ')}</div>
          {(fullMail?.cc?.length > 0) && <div><strong>CC:</strong> {fullMail.cc.map(r => r.email).join(', ')}</div>}
          <div><strong>Datum:</strong> {formatDateTime(fullMail?.date || mail.date)}</div>
          <div className="pu-modal__direction-badge">
            {mail.direction === 'sent'
              ? <span className="pu-mail__tag pu-mail__tag--sent">Gesendet</span>
              : <span className="pu-mail__tag pu-mail__tag--recv">Empfangen</span>
            }
            {mail.has_attachments && <span className="pu-mail__tag pu-mail__tag--attachment">Anhang</span>}
          </div>
        </div>
        <div className="pu-modal__body">
          {loading ? (
            <div className="pu-loading-inline">Lade Mail-Inhalt...</div>
          ) : fullMail?.body ? (
            fullMail.body_type === 'HTML' || fullMail.body_type === 'html'
              ? <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(fullMail.body) }} />
              : <pre className="pu-modal__text">{fullMail.body}</pre>
          ) : (
            <p className="pu-modal__preview">{mail.preview}</p>
          )}

          {mail.has_attachments && (
            <div className="pu-modal__attachments" style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid #e5e7eb' }}>
              <strong style={{ display: 'block', marginBottom: 8 }}>
                Anhänge ({attachments.length})
              </strong>
              {attachmentError && (
                <div style={{ background: '#fee2e2', color: '#991b1b', padding: '8px 12px', borderRadius: 6, marginBottom: 8, fontSize: 13 }}>
                  ⚠ {attachmentError}
                </div>
              )}
              {attachments.length === 0 && !attachmentError && (
                <div style={{ color: '#6b7280', fontSize: 13 }}>Lade Anhänge...</div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {attachments.map(att => (
                  <button
                    key={att.id}
                    onClick={() => handleDownload(att)}
                    disabled={downloadingId === att.id}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '8px 12px', border: '1px solid #d1d5db',
                      borderRadius: 6, background: '#f9fafb', cursor: 'pointer',
                      textAlign: 'left', fontSize: 14,
                    }}
                    title={`${att.content_type} · ${formatBytes(att.size)}`}
                  >
                    <span>📎</span>
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {att.name}
                    </span>
                    <span style={{ color: '#6b7280', fontSize: 12 }}>
                      {formatBytes(att.size)}
                    </span>
                    <span style={{ color: '#00a87e', fontSize: 12 }}>
                      {downloadingId === att.id ? 'Lade...' : 'Download'}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ── Hauptkomponente ──

export default function PostfachUebergabe() {
  const [token, setToken] = useState(sessionStorage.getItem('postfach_token'));
  const [mailbox, setMailbox] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searchError, setSearchError] = useState('');
  const [searching, setSearching] = useState(false);
  const [selectedMail, setSelectedMail] = useState(null);
  const [filterDirection, setFilterDirection] = useState('all'); // all, sent, received
  const [showAllgemein, setShowAllgemein] = useState(false);

  const handleAuth = useCallback((newToken, mb) => {
    sessionStorage.setItem('postfach_token', newToken);
    setToken(newToken);
    setMailbox(mb);
  }, []);

  const headers = { Authorization: `Bearer ${token}` };

  // Zusammenfassung laden
  const loadSummary = useCallback(async (forceRefresh = false) => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const url = `${API_URL}/postfach/summary${forceRefresh ? '?force_refresh=true' : ''}`;
      const res = await fetch(url, { headers });
      if (res.status === 401) {
        sessionStorage.removeItem('postfach_token');
        setToken(null);
        return;
      }
      if (!res.ok) throw new Error('Laden fehlgeschlagen');
      const result = await res.json();
      setData(result);
      setMailbox(result.mailbox);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { if (token) loadSummary(); }, [token, loadSummary]);

  // Suche
  const handleSearch = async () => {
    if (!searchQuery.trim() || searchQuery.length < 2) return;
    setSearching(true);
    setSearchError('');
    try {
      const res = await fetch(`${API_URL}/postfach/search?q=${encodeURIComponent(searchQuery)}`, { headers });
      if (res.ok) {
        const result = await res.json();
        setSearchResults(result.results || []);
      } else {
        const detail = await res.json().catch(() => ({}));
        setSearchError(detail.detail || `Fehler ${res.status}`);
        setSearchResults([]);
      }
    } catch (err) {
      setSearchError('Netzwerkfehler bei der Suche.');
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('postfach_token');
    setToken(null);
    setData(null);
  };

  // Nicht authentifiziert → Passwort-Gate
  if (!token) {
    return <PasswordGate onAuth={handleAuth} />;
  }

  // Laden
  if (loading && !data) {
    return (
      <div className="pu-loading">
        <div className="pu-loading__spinner" />
        <p className="pu-loading__text">Postfach wird analysiert...</p>
        <p className="pu-loading__sub">Mails werden aus allen Ordnern geladen und per KI nach Objekten sortiert. Das kann 30-60 Sekunden dauern.</p>
      </div>
    );
  }

  // Fehler
  if (error && !data) {
    return (
      <div className="pu-error">
        <h2>Fehler</h2>
        <p>{error}</p>
        <button onClick={() => loadSummary()} className="pu-btn">Erneut versuchen</button>
      </div>
    );
  }

  if (!data) return null;

  // Filter anwenden
  const filteredObjekte = data.objekte.map(obj => {
    if (filterDirection === 'all') return obj;
    return {
      ...obj,
      mails: obj.mails.filter(m => m.direction === filterDirection),
      mail_count: obj.mails.filter(m => m.direction === filterDirection).length,
    };
  }).filter(obj => obj.mail_count > 0);

  return (
    <div className="pu">
      {/* Header */}
      <div className="pu__header">
        <div className="pu__header-left">
          <div className="pu__logo">
            <img src="/assets/logos/one-immo-lockup-on-light.svg" alt="One. immo" />
          </div>
          <div className="pu__title-block">
            <h1 className="pu__title">Postfach-Übergabe</h1>
            <p className="pu__subtitle">{data.mailbox} &middot; Zeitraum {formatDate(data.stats.zeitraum_von)} bis {formatDate(data.stats.zeitraum_bis)}</p>
          </div>
        </div>
        <div className="pu__header-right">
          <span className="pu__generated">
            Stand: {formatDateTime(data.generated_at)}
          </span>
          <button
            className="pu-btn pu-btn--outline"
            onClick={() => loadSummary(true)}
            disabled={loading}
            title="Zusammenfassung neu generieren"
          >
            {loading ? 'Aktualisiere...' : 'Aktualisieren'}
          </button>
          <button className="pu-btn pu-btn--ghost" onClick={handleLogout}>Abmelden</button>
        </div>
      </div>

      {/* KPI-Leiste */}
      <StatsBar stats={data.stats} />

      {/* Toolbar: Suche + Filter */}
      <div className="pu__toolbar">
        <div className="pu__search">
          <input
            type="text"
            className="pu__search-input"
            placeholder="Mails durchsuchen (Betreff, Absender, Inhalt)..."
            value={searchQuery}
            onChange={e => { setSearchQuery(e.target.value); if (!e.target.value) setSearchResults(null); }}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
          />
          <button className="pu-btn pu-btn--sm" onClick={handleSearch} disabled={searching || searchQuery.length < 2}>
            {searching ? '...' : 'Suchen'}
          </button>
        </div>
        <div className="pu__filters">
          <button
            className={`pu-filter ${filterDirection === 'all' ? 'pu-filter--active' : ''}`}
            onClick={() => setFilterDirection('all')}
          >Alle</button>
          <button
            className={`pu-filter ${filterDirection === 'received' ? 'pu-filter--active' : ''}`}
            onClick={() => setFilterDirection('received')}
          >Empfangen</button>
          <button
            className={`pu-filter ${filterDirection === 'sent' ? 'pu-filter--active' : ''}`}
            onClick={() => setFilterDirection('sent')}
          >Gesendet</button>
        </div>
      </div>

      {/* Suchfehler */}
      {searchError && (
        <div className="pu__search-error" style={{ background:'#fee2e2', color:'#991b1b', padding:'12px 16px', borderRadius:8, margin:'12px 0' }}>
          ⚠ {searchError}
        </div>
      )}

      {/* Suchergebnisse */}
      {searchResults && (
        <div className="pu__search-results">
          <div className="pu__search-results-header">
            <h2>{searchResults.length} Ergebnis{searchResults.length !== 1 ? 'se' : ''} für "{searchQuery}"</h2>
            <button className="pu-btn pu-btn--ghost pu-btn--sm" onClick={() => { setSearchResults(null); setSearchQuery(''); setSearchError(''); }}>Schließen</button>
          </div>
          {searchResults.map((mail, i) => (
            <MailRow key={mail.id || i} mail={mail} onSelect={setSelectedMail} />
          ))}
        </div>
      )}

      {/* Objekt-Karten */}
      {!searchResults && (
        <div className="pu__objekte">
          <h2 className="pu__section-title">
            Nach Objekt sortiert
            <span className="pu__section-count">{filteredObjekte.length} Objekte</span>
          </h2>

          {filteredObjekte.map((obj, i) => (
            <ObjektCard
              key={obj.objekt_nr || i}
              objekt={obj}
              onSelectMail={setSelectedMail}
              defaultExpanded={i < 3}
            />
          ))}

          {/* Allgemein-Kategorie */}
          {data.allgemein?.mail_count > 0 && (
            <div className="pu-objekt pu-objekt--general">
              <div className="pu-objekt__header" onClick={() => setShowAllgemein(!showAllgemein)}>
                <div className="pu-objekt__title-row">
                  <h3 className="pu-objekt__title">Allgemein / Nicht zugeordnet</h3>
                </div>
                <div className="pu-objekt__sub-row">
                  <span className="pu-objekt__count">{data.allgemein.mail_count} Mails</span>
                  <svg className={`pu-objekt__chevron ${showAllgemein ? 'pu-objekt__chevron--open' : ''}`}
                    width="16" height="16" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </div>
              </div>
              {data.allgemein.zusammenfassung && (
                <div className="pu-objekt__summary"><p>{data.allgemein.zusammenfassung}</p></div>
              )}
              {showAllgemein && (
                <div className="pu-objekt__timeline">
                  {data.allgemein.mails.map((mail, i) => (
                    <MailRow key={mail.id || i} mail={mail} onSelect={setSelectedMail} />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Mail-Detail-Modal */}
      {selectedMail && (
        <MailDetail mail={selectedMail} onClose={() => setSelectedMail(null)} />
      )}
    </div>
  );
}
